import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ChevronRight, PauseOctagon, Play, RotateCcw } from "lucide-react";
import WorkspaceDetailDrawer from "@/components/superadmin/WorkspaceDetailDrawer";

function fmtCents(cents) {
  const n = Number(cents || 0);
  return `$${(n / 100).toFixed(4)}`;
}

export default function SuperadminWorkspaces() {
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [suspendedFilter, setSuspendedFilter] = useState("");
  const [sort, setSort] = useState("cost");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, page, limit: 25 };
      if (q.trim()) params.q = q.trim();
      if (planFilter) params.plan = planFilter;
      if (suspendedFilter !== "")
        params.suspended = suspendedFilter === "true";
      const { data: resp } = await api.get("/admin/workspaces", { params });
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, planFilter, suspendedFilter, sort, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="superadmin-workspaces">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-[#2C2A29]">
            Workspaces
          </h2>
          <p className="font-sans text-sm text-[#7A7571] mt-1">
            {data.total} total &middot; sorted by{" "}
            {sort === "cost"
              ? "lifetime cost"
              : sort === "activity"
              ? "last activity"
              : "creation date"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A5A29E]"
            strokeWidth={2}
          />
          <input
            data-testid="superadmin-workspaces-search"
            type="text"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            placeholder="Search name or slug…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A] transition-all"
          />
        </div>
        <select
          data-testid="superadmin-workspaces-plan-filter"
          value={planFilter}
          onChange={(e) => {
            setPage(1);
            setPlanFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
        >
          <option value="">All plans</option>
          <option value="free">free</option>
          <option value="team">team</option>
          <option value="team_plus">team_plus</option>
          <option value="grandfathered">grandfathered</option>
        </select>
        <select
          data-testid="superadmin-workspaces-suspended-filter"
          value={suspendedFilter}
          onChange={(e) => {
            setPage(1);
            setSuspendedFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
        >
          <option value="">Any status</option>
          <option value="false">Active only</option>
          <option value="true">Suspended only</option>
        </select>
        <select
          data-testid="superadmin-workspaces-sort"
          value={sort}
          onChange={(e) => {
            setPage(1);
            setSort(e.target.value);
          }}
          className="px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
        >
          <option value="cost">Sort: lifetime cost</option>
          <option value="activity">Sort: last activity</option>
          <option value="created">Sort: created</option>
        </select>
      </div>

      <div
        className="rounded-xl border border-[#DCD7CE] bg-white overflow-hidden"
        data-testid="superadmin-workspaces-table"
      >
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#DCD7CE] bg-[#FAF9F7] font-sans text-[10px] uppercase tracking-[0.16em] text-[#7A7571] font-semibold">
          <div className="col-span-4">Workspace</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-1 text-right">Members</div>
          <div className="col-span-2 text-right">Lifetime cost</div>
          <div className="col-span-2">Last activity</div>
          <div className="col-span-1" />
        </div>
        {loading ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-workspaces-loading"
          >
            Loading workspaces…
          </div>
        ) : data.items.length === 0 ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-workspaces-empty"
          >
            No workspaces match these filters.
          </div>
        ) : (
          data.items.map((w) => (
            <button
              key={w.id}
              onClick={() => setActiveId(w.id)}
              data-testid={`workspace-row-${w.id}`}
              className="w-full grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#EFEAE0] last:border-b-0 items-center hover:bg-[#FAF9F7] transition-colors text-left"
            >
              <div className="col-span-4 min-w-0">
                <div className="font-sans text-sm text-[#2C2A29] font-semibold truncate flex items-center gap-2">
                  {w.name}
                  {w.suspended ? (
                    <span
                      data-testid={`workspace-row-${w.id}-suspended-badge`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#8C3A1F]/10 text-[#8C3A1F]"
                    >
                      <PauseOctagon size={10} strokeWidth={2.5} />
                      Suspended
                    </span>
                  ) : null}
                </div>
                <div className="font-sans text-xs text-[#7A7571] truncate">
                  {w.owner_email || "—"}
                </div>
              </div>
              <div className="col-span-2">
                <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-mono text-[#2C2A29] bg-[#EFEAE0]">
                  {w.plan}
                </span>
                <div className="font-sans text-[10px] text-[#A5A29E] mt-1">
                  +{w.markup_rate_percent}% markup
                </div>
              </div>
              <div className="col-span-1 text-right font-sans text-sm text-[#2C2A29]">
                {w.member_count}
              </div>
              <div className="col-span-2 text-right font-mono text-sm text-[#2C2A29]">
                {fmtCents(w.lifetime_raw_cost_cents)}
                <div className="font-sans text-[10px] text-[#A5A29E]">
                  trial {fmtCents(w.trial_anthropic_cents_remaining)}
                </div>
              </div>
              <div className="col-span-2 font-sans text-xs text-[#7A7571]">
                {w.last_activity_at
                  ? new Date(w.last_activity_at).toLocaleDateString()
                  : "—"}
              </div>
              <div className="col-span-1 flex justify-end">
                <ChevronRight
                  size={16}
                  className="text-[#A5A29E]"
                  strokeWidth={2}
                />
              </div>
            </button>
          ))
        )}
      </div>

      {data.total > 25 ? (
        <div className="flex items-center justify-between mt-4">
          <p className="font-sans text-xs text-[#7A7571]">
            Page {page} &middot; {data.items.length} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="superadmin-workspaces-prev"
              className="px-3 py-1.5 rounded-md font-sans text-xs border border-[#DCD7CE] bg-white disabled:opacity-40 hover:bg-[#EFEAE0] transition-colors"
            >
              Previous
            </button>
            <button
              disabled={!data.has_more}
              onClick={() => setPage((p) => p + 1)}
              data-testid="superadmin-workspaces-next"
              className="px-3 py-1.5 rounded-md font-sans text-xs border border-[#DCD7CE] bg-white disabled:opacity-40 hover:bg-[#EFEAE0] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      <WorkspaceDetailDrawer
        workspaceId={activeId}
        onClose={() => setActiveId(null)}
        onChanged={() => load()}
      />

      <p className="font-sans text-xs text-[#A5A29E] mt-4 inline-flex items-center gap-1.5">
        <RotateCcw size={11} strokeWidth={2} />
        Use the detail drawer to suspend, change plan, adjust trial, or reset
        markup. All changes are audit-logged.
      </p>
    </div>
  );
}
